import { type RoomSummaryQuery } from "@crowdcoursing/api/graphql";
import { Box, Card, CardContent, Divider, Paper, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";

import { Route } from "~services/router/Route";

// Centralized strings for internationalization
const tCardTitle = "📅 Upcoming Events";
const tNoEvents = "No upcoming events";

type RoomFromQuery = NonNullable<RoomSummaryQuery["room"]>;

export interface UpcomingEventsCardProps {
  room: RoomFromQuery;
}

export const UpcomingEventsCard = ({ room }: UpcomingEventsCardProps) => {
  const navigate = useNavigate();
  const sessions = room.sessions || [];
  // Filter sessions to only include those with upcoming meeting times
  const upcomingSessions = sessions.filter((session) => session.nextMeetingTime);

  const handleEventClick = (eventId: string) => {
    navigate(Route.eventView({ eventId }));
  };

  return (
    <Card sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h2" component="h2" sx={{ fontSize: "1.25rem", fontWeight: "bold" }} gutterBottom>
          {tCardTitle}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Box
          sx={{
            maxHeight: "300px",
            overflow: "auto",
          }}
        >
          <Stack spacing={1}>
            {upcomingSessions.map((session) => {
              if (!session.nextMeetingTime) {
                return null;
              } // This should never happen due to filter, but satisfies TS

              return (
                <Paper
                  key={session.id}
                  sx={{
                    "&:hover": {
                      backgroundColor: "action.hover",
                      transform: "translateY(-2px)",
                      transition: "all 0.2s ease-in-out",
                    },
                    cursor: "pointer",
                    p: 2,
                  }}
                  onClick={() => handleEventClick(session.id)}
                >
                  <Box sx={{ alignItems: "center", display: "flex", gap: 2, justifyContent: "space-between" }}>
                    <Typography
                      variant="h3"
                      component="h3"
                      sx={{ flex: 1, fontSize: "1rem", fontWeight: "bold", minWidth: 0 }}
                    >
                      {session.name}
                    </Typography>
                    <Box sx={{ alignItems: "flex-end", display: "flex", flexDirection: "column", textAlign: "right" }}>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                        {new Date(session.nextMeetingTime.beginMeetingTime).toLocaleDateString([], {
                          day: "numeric",
                          month: "short",
                        })}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                        {new Date(session.nextMeetingTime.beginMeetingTime).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              );
            })}
            {upcomingSessions.length === 0 && (
              <Typography variant="body2" color="text.secondary" textAlign="center">
                {tNoEvents}
              </Typography>
            )}
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};
